<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, onUnmounted } from 'vue';
import { useSmarticoMiniGamesHistory } from '~/stores/smartico/miniGamesHistory';
import { useSmarticoMiniGames } from '~/stores/smartico/miniGames';
import { useDateTimeHelper } from '~/composables/useDateTimeHelper';

const MILLISECONDS_THRESHOLD = 1000000000000;
const UPDATE_INTERVAL_MS = 1000;

const initialItemsToShow = 5;
const itemsToShow = ref(initialItemsToShow);

const { openLogin } = useAuth();
const { isAuthenticated } = storeToRefs(useAuthStore());

enum SAWGameType {
  SpinAWheel = 1,
  ScratchCard = 2,
  MatchX = 3,
  GiftBox = 4,
  PrizeDrop = 5,
  Quiz = 6,
  LootboxWeekdays = 7,
  LootboxCalendarDays = 8,
  TreasureHunt = 9,
  Voyager = 10,
}

const smarticoMiniGamesStore = useSmarticoMiniGames();
const { playMiniGame } = smarticoMiniGamesStore;

const currentUnixTimestamp = ref(getCurrentUnixTimestamp());
let timeUpdateInterval: NodeJS.Timeout | null = null;

function getCurrentUnixTimestamp(): number {
  return Math.floor(Date.now() / 1000);
}

function isTimestampInMilliseconds(timestamp: number): boolean {
  return timestamp > MILLISECONDS_THRESHOLD;
}

function convertMillisecondsToSeconds(timestamp: number): number {
  return Math.floor(timestamp / 1000);
}

function normalizeTimestamp(timestamp: number): number {
  return isTimestampInMilliseconds(timestamp)
    ? convertMillisecondsToSeconds(timestamp)
    : timestamp;
}

function canUserPlayMiniGame(game: TMiniGameHistoryItem): boolean {
  const { next_available_spin_ts } = game.template;

  if (!next_available_spin_ts) {
    return true;
  }

  const normalizedNextAvailableTime = normalizeTimestamp(
    next_available_spin_ts,
  );

  return currentUnixTimestamp.value >= normalizedNextAvailableTime;
}

async function handlePlayMiniGame(game: TMiniGameHistoryItem): Promise<void> {
  if (!canUserPlayMiniGame(game)) {
    return;
  }

  if (!isAuthenticated.value) {
    openLogin();

    return;
  }

  await playMiniGame(game.saw_template_id);
}

function getButtonText(game: TMiniGameHistoryItem) {
  if (!canUserPlayMiniGame(game)) {
    return 'Limite atingido';
  }

  switch (game.template.saw_game_type_id) {
    case SAWGameType.SpinAWheel:
      return 'GIRAR NOVAMENTE';

    case SAWGameType.ScratchCard:
      return 'RASPAR NOVAMENTE';

    case SAWGameType.MatchX:
      return 'PALPITAR NOVAMENTE';

    case SAWGameType.Quiz:
      return 'RESPONDER NOVAMENTE';

    case SAWGameType.GiftBox:
      return 'ABRIR NOVAMENTE';

    default:
      return 'JOGAR NOVAMENTE';
  }
}

const smarticoHistoryStore = useSmarticoMiniGamesHistory();
const { getFormattedDateFromEpoch } = useDateTimeHelper();
const { historyGames, isLoading, hasMore } = storeToRefs(smarticoHistoryStore);
const { loadHistory, loadMore } = smarticoHistoryStore;

const ribbonTab = ref<string | number>('all');

watch(ribbonTab, () => {
  resetItems();
});

const filteredHistoryGames = computed(() => {
  let games = [];

  if (ribbonTab.value === 'all') {
    games = historyGames.value;
  } else {
    games = historyGames.value.filter(
      (game) => game.template.saw_game_type_id === ribbonTab.value,
    );
  }

  return games.slice(0, itemsToShow.value);
});

const totalFilteredGamesCount = computed(() => {
  if (ribbonTab.value === 'all') {
    return historyGames.value.length;
  }

  return historyGames.value.filter(
    (game) => game.template.saw_game_type_id === ribbonTab.value,
  ).length;
});

const hasMoreItemsToLoad = computed(() => {
  return itemsToShow.value < totalFilteredGamesCount.value || hasMore.value;
});

const canShowLess = computed(() => {
  return itemsToShow.value > initialItemsToShow;
});

async function loadMoreItems() {
  itemsToShow.value += 5;

  // quando o cache local acaba, busca a próxima página no servidor
  if (itemsToShow.value >= historyGames.value.length && hasMore.value) {
    await loadMore();
  }
}

const historyContainerRef = ref<HTMLElement | null>(null);

function resetItems() {
  itemsToShow.value = initialItemsToShow;
}

function showLessItemsAndScroll() {
  resetItems();
  if (historyContainerRef.value) {
    historyContainerRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}

function getPrizeName(game: TMiniGameHistoryItem) {
  if (game.template.prizesMap && game.template.prizesMap[game.saw_prize_id]) {
    const prize = game.template.prizesMap[game.saw_prize_id];

    if (prize && prize.saw_prize_ui_definition) {
      return prize.saw_prize_ui_definition.custom_data;
    }
  }

  return '---';
}

function startTimeUpdater(): void {
  timeUpdateInterval = setInterval(() => {
    currentUnixTimestamp.value = getCurrentUnixTimestamp();
  }, UPDATE_INTERVAL_MS);
}

function stopTimeUpdater(): void {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
    timeUpdateInterval = null;
  }
}

onMounted(async () => {
  startTimeUpdater();
  await loadHistory();
});

onUnmounted(stopTimeUpdater);
</script>

<template>
  <!-- Tabs removidas - usa as tabs do componente pai (MiniGames.vue) -->

  <div
    v-if="isLoading"
    class="text-center text-secondary mt-1 mb-1 loading-pulse"
  >
    Carregando histórico...
  </div>
  <div
    v-else-if="filteredHistoryGames.length > 0"
    ref="historyContainerRef"
  >
    <div
      v-for="game in filteredHistoryGames"
      :key="game.client_request_id"
    >
      <div
        class="flex flex-wrap justify-start gap-2 lg:justify-between items-center self-stretch border [background:#010612] backdrop-blur-[10px] p-2 lg:p-5 rounded-[10px] border-solid border-[#1D2329] mb-2.5"
      >
        <div class="flex items-center w-1/2 lg:w-1/3 gap-1">
          <NuxtImg
            :alt="game.template.saw_template_ui_definition.name"
            class="w-[35px] h-[35px] lg:w-[55px] lg:h-[55px] mr-3 object-cover rounded-lg"
            :src="game.template.saw_template_ui_definition.promo_image"
          />
          <div class="flex flex-col gap-1 min-w-0">
            <span
              class="self-stretch text-[rgba(255,255,255,0.50)] text-[10px] font-semibold leading-4"
              >MINI GAME</span
            >
            <span
              class="self-stretch text-white text-[12px] lg:text-[15px] font-semibold leading-[19px] overflow-hidden whitespace-nowrap text-ellipsis lg:min-w-[250px] max-w-[160px] lg:max-w-[260px]"
            >
              {{ game.template.saw_template_ui_definition.name }}
            </span>
          </div>
        </div>
        <div class="flex flex-col gap-1 w-auto lg:w-1/4 min-w-0">
          <span
            class="self-stretch text-[rgba(255,255,255,0.50)] text-[10px] font-semibold leading-4"
            >PRÊMIO</span
          >
          <span
            class="self-stretch text-white text-[12px] lg:text-[15px] font-semibold leading-[19px] overflow-hidden whitespace-nowrap text-ellipsis flex-shrink-0 max-w-[160px] lg:max-w-full"
          >
            {{ getPrizeName(game) ?? '---' }}
          </span>
        </div>
        <div
          class="flex justify-between lg:justify-center items-center gap-[15px] lg:gap-[30px] [background:rgba(255,255,255,0.05)] px-[10px] lg:px-[15px] py-2 lg:py-2.5 rounded-[10px] w-full lg:w-auto mt-2.5 lg:mt-0"
        >
          <div class="flex flex-col gap-[5px]">
            <span
              class="self-stretch text-[rgba(255,255,255,0.50)] text-[10px] font-semibold leading-4"
            >
              <span class="hidden lg:inline">DATA DE </span>RECEBIMENTO
            </span>
            <span
              class="self-stretch text-white text-[12px] lg:text-[15px] font-semibold leading-[15px]"
            >
              {{ getFormattedDateFromEpoch(game.acknowledge_date_ts) }}
            </span>
          </div>
          <button
            class="bg-[#FF5722] text-white px-3 py-3 text-xs rounded-md flex items-center justify-center font-semibold gap-[7px] lg:px-6 lg:py-3 lg:text-sm min-w-[190px] lg:min-w-[208px]"
            @click="handlePlayMiniGame(game)"
          >
            <p
              class="text-[color:var(--White,#FFF)] text-center text-xs font-extrabold leading-3 whitespace-nowrap overflow-hidden text-ellipsis"
              >{{ getButtonText(game) }}</p
            >
            <NuxtIcon name="rejoin" />
          </button>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="
      (hasMoreItemsToLoad || canShowLess) &&
      totalFilteredGamesCount > 0 &&
      !isLoading
    "
    class="flex justify-center gap-4"
  >
    <button
      v-if="canShowLess"
      class="toggleButton"
      @click="showLessItemsAndScroll"
    >
      <span class="text-white text-[13px] font-bold leading-4">Ver Menos</span>
    </button>
    <button
      v-if="hasMoreItemsToLoad"
      class="toggleButton"
      @click="loadMoreItems"
    >
      <span class="text-white text-[13px] font-bold leading-4">Ver Mais</span>
    </button>
  </div>

  <div
    v-else-if="!isLoading"
    class="alert alert-info"
  >
    <span>Nenhum histórico de mini-games encontrado.</span>
  </div>
</template>

<style scoped lang="postcss">
.tabs {
  @apply flex justify-start sm:justify-center mb-2 lg:mb-4 border-b border-[#424344] gap-[5px];
}

.tabsContent {
  @apply flex overflow-x-auto gap-[5px];

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 639px) {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      #000 5px,
      #000 90%,
      transparent
    );
    mask-image: linear-gradient(
      to right,
      transparent,
      #000 2px,
      #000 90%,
      transparent
    );
  }
}
.tabButton {
  @apply px-[15px] py-[10px] text-white/70 flex-shrink-0 rounded-[6px] font-semibold;
}
.activeTab {
  @apply text-[#00c9ff] bg-[rgba(0,201,255,0.1)] font-semibold;
}

@media (max-width: 639px) {
  .tabButton {
    @apply px-[10px] py-[5px] text-[13px] font-semibold;
  }
}

.toggleButton {
  @apply flex w-[1114px] h-[41px] justify-center items-center gap-[15px] border [background:#010612] backdrop-blur-[10px] p-5 rounded-[10px] border-solid border-[#1D2329] text-white;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.loading-pulse {
  animation: pulse 1.5s infinite ease-in-out;
}
</style>
